import { useEffect, useState } from 'react';
import * as Tooltip from '~/components/ui/Tooltip';
import type { RepoFullName } from '~/types/github';
import { debounce } from '~/utils/debounce';
import { useInitRef } from '~/utils/init-ref';

export function GithubStatusPopover({
  status,
  repository,
  branches,
  branchName,
  onSwitch,
  onCreate,
}: {
  status: 'pending' | 'success';
  repository: RepoFullName;
  branchName: string;
  branches?: string[];
  onSwitch: (branch: string) => void;
  onCreate: () => void;
}) {
  // smooth status transitions to avoid flicker
  const [smoothStatus, setSmoothStatus] = useState(status);
  const updaterRef = useInitRef(() => debounce((status) => setSmoothStatus(status), 200));
  const updateStatus = updaterRef.current;

  useEffect(() => {
    updateStatus(status);
  }, [status]);

  const color =
    smoothStatus === 'pending'
      ? {
          stroke: 'red',
          fill: 'red',
        }
      : {
          stroke: '#22C55E',
          fill: '#16A34A',
        };

  return (
    <div className="z-dialog bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 p-3 min-w-68 rounded-md border border-bolt-elements-borderColor">
      <h1 className="mb-2 text-sm">GitHub status</h1>

      <div className="bg-bolt-elements-background-depth-4 p-2 rounded text-sm">
        <svg className="inline mr-3" width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle cx="6.5" cy="6.5" r="2.5" fill={color.fill} />
          <circle cx="6.5" cy="6.5" r="4.33333" stroke={color.stroke} strokeOpacity="0.2" strokeWidth="3.66667" />
        </svg>
        {smoothStatus === 'pending' ? 'Syncing' : 'Synced'} to GitHub
      </div>

      <div className="py-3 flex flex-col text-lg my-3 rounded bg-bolt-elements-background-depth-4 max-h-110 overflow-y-auto">
        <div className="px-3 text-xs text-bolt-elements-textSecondary"> Active branch </div>

        <div className="px-3 text-sm">
          {repository.name}/{branchName}
        </div>

        <hr className="my-2 border-bolt-elements-borderColor w-100%" />

        <div
          className="cursor-pointer mx-1.5 px-2 py-1 text-bolt-elements-textSecondary flex items-center justify-between hover:bg-bolt-elements-background-depth-3 rounded-md"
          onClick={onCreate}
        >
          <div className="text-sm">Create new branch</div>
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <div className="i-heroicons:plus" />
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content sideOffset={2}>Create new branch</Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>

        <div className="mx-1.5 px-2 py-1 text-bolt-elements-textSecondary flex items-center justify-between">
          <div className="text-sm">
            {branchName}{' '}
            <div className="ml-2 inline-block text-xs rounded bg-bolt-ds-brandContainer text-bolt-ds-brand px-1.5 py-0.5">
              active
            </div>
          </div>
        </div>

        {branches
          ?.filter((branch) => branch !== branchName)
          .map((branch) => (
            <div
              className="cursor-pointer mx-1.5 px-2 py-1 text-bolt-elements-textSecondary flex items-center justify-between hover:bg-bolt-elements-background-depth-3 rounded-md"
              key={branch}
              onClick={() => onSwitch(branch)}
            >
              <div className="text-sm">{branch}</div>
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <div className="i-heroicons:arrows-right-left" />
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content sideOffset={2}>Switch to branch {branch}</Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          ))}
      </div>

      <a
        target="_blank"
        className="flex items-center w-full block text-lg my-3 rounded bg-bolt-elements-background-depth-4 p-3 hover:bg-bolt-elements-background-depth-3"
        href={`https://github.com/${repository.owner}/${repository.name}`}
      >
        <div className="flex-1 text-sm">
          <span>Go to repository</span>
          <br />
          <span className="text-xs text-bolt-elements-textSecondary">
            {repository.owner}/{repository.name}
          </span>
        </div>
        <div className="i-ph:arrow-up-right-bold text-bolt-elements-textSecondary" />
      </a>

      <a
        href="https://support.bolt.new/integrations/git"
        target="_blank"
        className="underline text-bolt-elements-textSecondary text-sm"
      >
        View GitHub Documentation
      </a>
    </div>
  );
}
